/**
 * Selos e tons do financeiro (visual v2): situação, tipo de lançamento e cor
 * de valores com os tokens do tema — sem cores fixas.
 */
import type { ReactNode } from 'react'
import { ArrowDownCircle, ArrowUpCircle } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/utils/cn'

export type FinanceBadgeVariant = 'default' | 'secondary' | 'outline' | 'destructive'

/** Tom por natureza do valor: entrada (receita) em destaque, saída (despesa) em vermelho do tema. */
export type AmountTone = 'income' | 'expense' | 'neutral'

const AMOUNT_TONE_CLASS: Record<AmountTone, string> = {
  income: 'text-primary',
  expense: 'text-destructive',
  neutral: 'text-foreground',
}

export function amountToneClass(tone: AmountTone): string {
  return AMOUNT_TONE_CLASS[tone]
}

/** INCOME → 'income'; EXPENSE → 'expense'; qualquer outro → 'neutral'. */
export function toneForTransactionType(type: string | null | undefined): AmountTone {
  if (type === 'INCOME') return 'income'
  if (type === 'EXPENSE') return 'expense'
  return 'neutral'
}

export function AmountText({ tone, className, children }: { tone: AmountTone; className?: string; children: ReactNode }) {
  return <span className={cn('tabular-nums font-medium', AMOUNT_TONE_CLASS[tone], className)}>{children}</span>
}

export function TransactionTypeIcon({ type, className }: { type: string; className?: string }) {
  const Icon = type === 'INCOME' ? ArrowUpCircle : ArrowDownCircle
  return <Icon className={cn('size-5 shrink-0', AMOUNT_TONE_CLASS[toneForTransactionType(type)], className)} aria-hidden="true" />
}

/**
 * Variante do selo por situação (códigos dos tipos do financeiro ou rótulos já traduzidos).
 * Pago/concluído → secondary; aberto/a vencer/ativo → outline; vencido → destructive;
 * cancelado → outline esmaecido (ver FinanceStatusBadge).
 */
const STATUS_VARIANT: Record<string, FinanceBadgeVariant> = {
  // códigos
  PENDING: 'outline',
  OPEN: 'outline',
  ACTIVE: 'default',
  PAID: 'secondary',
  PARTIALLY_PAID: 'outline',
  COMPLETED: 'secondary',
  COLLECTED: 'secondary',
  INVOICED: 'secondary',
  OVERDUE: 'destructive',
  CANCELLED: 'outline',
  // rótulos
  'A vencer': 'outline',
  Aberto: 'outline',
  Aberta: 'outline',
  Pendente: 'outline',
  Ativo: 'default',
  Ativa: 'default',
  Pago: 'secondary',
  Paga: 'secondary',
  Recebido: 'secondary',
  Faturado: 'secondary',
  Concluído: 'secondary',
  Concluída: 'secondary',
  Vencido: 'destructive',
  Vencida: 'destructive',
  Cancelado: 'outline',
  Cancelada: 'outline',
}

const MUTED_STATUS = new Set(['CANCELLED', 'Cancelado', 'Cancelada'])

export function financeStatusVariant(status: string): FinanceBadgeVariant {
  return STATUS_VARIANT[status] ?? 'secondary'
}

/** Selo de situação; `status` escolhe a cor, `children` (opcional) é o texto exibido. */
export function FinanceStatusBadge({ status, className, children }: { status: string; className?: string; children?: ReactNode }) {
  return (
    <Badge
      variant={financeStatusVariant(status)}
      className={cn(MUTED_STATUS.has(status) && 'text-muted-foreground', className)}
    >
      {children ?? status}
    </Badge>
  )
}
